import { Button, message, Switch } from 'antd'
import React, { useEffect, useState } from 'react'
import { APIFunctions } from '../../lib/apiFunctions'
import { User } from '../../types/session'
import { OrderedreportId } from './types'
import { RightOutlined } from '@ant-design/icons'

export default function OverviewConfigureReport({
    apiFunctions,
    userData,
    reportIdInfo,
    resetTaxYearData,
    switchToReportTab,
}: {
    userData: User | null
    apiFunctions: APIFunctions
    reportIdInfo: OrderedreportId
    resetTaxYearData: () => void
    switchToReportTab: (reportId: string) => void
}) {
    const [docIncluded, setDocIncluded] = useState<boolean>(reportIdInfo.include_documentation)
    const [docLoading, setDocLoading] = useState<boolean>(false)

    const [notesIncluded, setNotesIncluded] = useState<boolean>(reportIdInfo.include_notes)
    const [notesLoading, setNotesLoading] = useState<boolean>(false)

    const [locationsIncluded, setLocationsIncluded] = useState<boolean>(
        reportIdInfo.include_locations
    )
    const [locationsLoading, setLocationsLoading] = useState<boolean>(false)

    const [rosterIncluded, setRosterIncluded] = useState<boolean>(reportIdInfo.include_roster)
    const [rosterLoading, setRosterLoading] = useState<boolean>(false)

    useEffect(() => {
        setDocIncluded(reportIdInfo.include_documentation)
        setNotesIncluded(reportIdInfo.include_notes)
        setRosterIncluded(reportIdInfo.include_roster)
    }, [reportIdInfo])

    async function changeReportDocInclude() {
        setDocLoading(true)
        try {
            const data = await apiFunctions.changeDocIncludeReportSwitch(
                userData?.user_id,
                reportIdInfo.id
            )
            setDocIncluded(data.is_included)
            resetTaxYearData()
        } catch (ex) {
            message.error('Could not switch')
        } finally {
            setDocLoading(false)
        }
    }

    async function changeReportNotesInclude() {
        setNotesLoading(true)
        try {
            const data = await apiFunctions.changeNotesIncludeReportSwitch(
                userData?.user_id,
                reportIdInfo.id
            )
            setNotesIncluded(data.is_included)
            resetTaxYearData()
        } catch (ex) {
            message.error('Could not switch')
        } finally {
            setNotesLoading(false)
        }
    }

    async function changeReportLocationsInclude() {
        setLocationsLoading(true)
        try {
            const data = await apiFunctions.changeLocationsIncludeReportSwitch(
                userData?.user_id,
                reportIdInfo.id
            )
            setLocationsIncluded(data.is_included)
            resetTaxYearData()
        } catch (ex) {
            message.error('Could not switch')
        } finally {
            setLocationsLoading(false)
        }
    }

    async function changeReportRosterInclude() {
        setRosterLoading(true)
        try {
            const data = await apiFunctions.changeRosterIncludeReportSwitch(
                userData?.user_id,
                reportIdInfo.id
            )
            setRosterIncluded(data.is_included)
            resetTaxYearData()
        } catch (ex) {
            message.error('Could not switch')
        } finally {
            setRosterLoading(false)
        }
    }

    return (
        <>
            <Button
                size="small"
                onClick={() => {
                    switchToReportTab(reportIdInfo.id)
                }}
                icon={<RightOutlined />}
            >
                Go to report
            </Button>
            <h4>Configuration:</h4>
            {reportIdInfo.is_locked && <p>You cannot change this when the report is locked</p>}
            <span>
                <Switch
                    disabled={reportIdInfo.is_locked}
                    loading={docLoading}
                    onChange={() => {
                        changeReportDocInclude()
                    }}
                    checked={docIncluded}
                />{' '}
                Include documentation
            </span>
            <br />
            <span>
                <Switch
                    disabled={reportIdInfo.is_locked}
                    loading={notesLoading}
                    onChange={() => {
                        changeReportNotesInclude()
                    }}
                    checked={notesIncluded}
                />{' '}
                Include notes
            </span>
            <br />
            <span>
                <Switch
                    disabled={reportIdInfo.is_locked}
                    loading={locationsLoading}
                    onChange={() => {
                        changeReportLocationsInclude()
                    }}
                    checked={locationsIncluded}
                />{' '}
                Include locations
            </span>
            {userData?.feature_flags && userData?.feature_flags?.split(' ').includes('roster') && (
                <>
                    <br />
                    <span>
                        <Switch
                            disabled={reportIdInfo.is_locked}
                            loading={rosterLoading}
                            onChange={() => {
                                changeReportRosterInclude()
                            }}
                            checked={rosterIncluded}
                        />{' '}
                        Include roster
                    </span>
                </>
            )}
        </>
    )
}
